import { useState, useEffect } from 'react';
import { Home, ChevronDown } from 'lucide-react';
import { api } from '@/App';

// Hook pour gérer les maisons
export const useHouseSelector = () => {
  const [currentHouse, setCurrentHouse] = useState(null);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHouses();
  }, []);

  const loadHouses = async () => {
    try {
      const response = await api.get('/houses');
      const housesList = response.data;
      
      setHouses(housesList);
      
      // Charger la maison sauvegardée ou prendre la première
      const savedHouseId = localStorage.getItem('currentHouseId');
      if (savedHouseId) {
        const saved = housesList.find(h => h.id === savedHouseId);
        if (saved) {
          setCurrentHouse(saved);
          localStorage.setItem('currentHouseAddress', saved.address || 'adresse non configurée');
        } else if (housesList.length > 0) {
          setCurrentHouse(housesList[0]);
          localStorage.setItem('currentHouseId', housesList[0].id);
          localStorage.setItem('currentHouseAddress', housesList[0].address || 'adresse non configurée');
        }
      } else if (housesList.length > 0) {
        setCurrentHouse(housesList[0]);
        localStorage.setItem('currentHouseId', housesList[0].id);
        localStorage.setItem('currentHouseAddress', housesList[0].address || 'adresse non configurée');
      }
    } catch (error) {
      console.error('Error loading houses:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectHouse = (house) => {
    setCurrentHouse(house);
    localStorage.setItem('currentHouseId', house.id);
    localStorage.setItem('currentHouseAddress', house.address || 'adresse non configurée');
    // Recharger la page pour mettre à jour tous les appareils
    window.location.reload();
  };

  const createHouse = async (name, address = '', description = '') => {
    try {
      const response = await api.post('/houses', {
        name,
        address,
        description,
      });
      await loadHouses();
      return response.data;
    } catch (error) {
      console.error('Error creating house:', error);
      throw error;
    }
  };

  return { 
    currentHouse, 
    houses, 
    loading, 
    selectHouse, 
    createHouse,
    reloadHouses: loadHouses
  };
};

// Composant sélecteur de maison (compact pour header)
export const HouseSelector = ({ compact = true }) => {
  const { currentHouse, houses, selectHouse } = useHouseSelector();
  const [showDropdown, setShowDropdown] = useState(false);

  if (!currentHouse) {
    return (
      <div className="flex items-center space-x-1 text-[10px] font-mono text-gray-500 px-2 py-1">
        <Home className="w-3 h-3" />
        <span>...</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="w-full flex items-center justify-between px-2 py-1.5 bg-black/50 border border-cyan-400/50 rounded text-[10px] font-mono hover:bg-black/70 transition-all"
      >
        <span className="flex items-center space-x-1 truncate">
          <Home className="w-3 h-3 text-cyan-400 flex-shrink-0" />
          <span className="text-cyan-400 font-bold uppercase truncate">{currentHouse.name}</span>
        </span>
        <ChevronDown className="w-3 h-3 text-cyan-400 flex-shrink-0 ml-1" />
      </button>
      
      {showDropdown && houses.length > 1 && (
        <div 
          className="absolute top-full left-0 mt-1 w-full min-w-[120px] bg-black border border-cyan-400 rounded z-50 overflow-hidden"
          style={{ boxShadow: '0 0 15px rgba(0, 240, 255, 0.4)' }}
        >
          {houses.map((house) => (
            <button
              key={house.id}
              onClick={() => {
                selectHouse(house);
                setShowDropdown(false);
              }}
              className={`w-full text-left px-2 py-1.5 font-mono text-[10px] transition-all ${
                house.id === currentHouse.id
                  ? 'bg-cyan-400 text-black'
                  : 'text-cyan-400 hover:bg-cyan-400/20'
              }`}
            >
              <Home className="w-3 h-3 inline mr-1" />
              {house.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default { useHouseSelector, HouseSelector };
